import React, { useState, useEffect, useMemo } from 'react';
import { Calendar, ChevronLeft, ChevronRight, Download, Filter } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Leave type colors and labels
const LEAVE_TYPES = {
  annual: { code: 'V', label: 'Vacation/Annual', color: 'bg-green-500', textColor: 'text-white' },
  sick: { code: 'S', label: 'Sick Leave', color: 'bg-yellow-600', textColor: 'text-white' },
  public_holiday: { code: 'PH', label: 'Public Holiday', color: 'bg-orange-500', textColor: 'text-white' },
  emergency: { code: 'E', label: 'Emergency', color: 'bg-red-500', textColor: 'text-white' },
  unpaid: { code: 'U', label: 'Unpaid', color: 'bg-gray-500', textColor: 'text-white' },
  mgmt_unpaid: { code: 'MU', label: 'MGMT Unpaid', color: 'bg-amber-500', textColor: 'text-white' },
  partial_unpaid: { code: 'PU', label: 'Partial Unpaid', color: 'bg-violet-500', textColor: 'text-white' },
  maternity: { code: 'M', label: 'Maternity', color: 'bg-pink-500', textColor: 'text-white' },
  paternity: { code: 'F', label: 'Paternity', color: 'bg-blue-500', textColor: 'text-white' },
};

const LeaveCalendar = () => {
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [viewMode, setViewMode] = useState('month'); // 'month' or 'year'
  const [leaves, setLeaves] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [departmentFilter, setDepartmentFilter] = useState('all');

  // Get days in month
  const daysInMonth = useMemo(() => {
    return new Date(year, month, 0).getDate();
  }, [year, month]);

  // Generate array of day numbers with day of week
  const calendarDays = useMemo(() => {
    const days = [];
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month - 1, d);
      days.push({
        day: d,
        dayOfWeek: DAYS_OF_WEEK[date.getDay()],
        isWeekend: date.getDay() === 0 || date.getDay() === 6
      });
    }
    return days;
  }, [year, month, daysInMonth]);

  // Load data
  useEffect(() => {
    loadData();
  }, [year, month, viewMode]);

  const loadData = async () => {
    setLoading(true);
    try {
      let leavesRes;
      if (viewMode === 'year') {
        // Fetch all leaves for the entire year
        leavesRes = await axios.get(`${API_URL}/api/leaves/calendar/year/${year}`);
      } else {
        leavesRes = await axios.get(`${API_URL}/api/leaves/calendar/${year}/${month}`);
      }
      const employeesRes = await axios.get(`${API_URL}/api/employees`);
      
      setLeaves(leavesRes.data);
      // Filter only active employees
      const activeEmployees = employeesRes.data.filter(e => 
        e.is_active && !['Resigned', 'Terminated'].includes(e.employment_status)
      );
      setEmployees(activeEmployees);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({ title: 'Error', description: 'Failed to load calendar data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Get unique departments
  const departments = useMemo(() => {
    const depts = [...new Set(employees.map(e => e.department).filter(Boolean))];
    return depts.sort();
  }, [employees]);

  // Filter employees by department
  const filteredEmployees = useMemo(() => {
    if (departmentFilter === 'all') return employees;
    return employees.filter(e => e.department === departmentFilter);
  }, [employees, departmentFilter]);

  // Build leave map: employee_id -> day -> leave_type (for monthly view)
  // Or employee_id -> month -> day -> leave_type (for yearly view)
  const leaveMap = useMemo(() => {
    const map = {};
    leaves.forEach(leave => {
      const empId = leave.employee_id;
      if (!map[empId]) map[empId] = {};
      
      // Parse dates and fill in all days
      const startDate = new Date(leave.start_date);
      const endDate = new Date(leave.end_date);
      
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        if (viewMode === 'year') {
          // For yearly view, store by month then day
          if (d.getFullYear() === year) {
            const m = d.getMonth() + 1;
            const dayNum = d.getDate();
            if (!map[empId][m]) map[empId][m] = {};
            map[empId][m][dayNum] = leave.leave_type;
          }
        } else {
          // For monthly view
          if (d.getMonth() + 1 === month && d.getFullYear() === year) {
            const dayNum = d.getDate();
            map[empId][dayNum] = leave.leave_type;
          }
        }
      }
    });
    return map;
  }, [leaves, month, year, viewMode]);

  // Calculate daily totals (for monthly view)
  const dailyTotals = useMemo(() => {
    if (viewMode === 'year') return {};
    const totals = {};
    for (let d = 1; d <= daysInMonth; d++) {
      totals[d] = 0;
      filteredEmployees.forEach(emp => {
        if (leaveMap[emp.id]?.[d]) {
          totals[d]++;
        }
      });
    }
    return totals;
  }, [filteredEmployees, leaveMap, daysInMonth, viewMode]);

  // Calculate employee totals (for monthly view)
  const employeeTotals = useMemo(() => {
    if (viewMode === 'year') return {};
    const totals = {};
    filteredEmployees.forEach(emp => {
      totals[emp.id] = 0;
      for (let d = 1; d <= daysInMonth; d++) {
        if (leaveMap[emp.id]?.[d]) {
          totals[emp.id]++;
        }
      }
    });
    return totals;
  }, [filteredEmployees, leaveMap, daysInMonth, viewMode]);

  // Calculate yearly totals per employee (for yearly view)
  const yearlyEmployeeTotals = useMemo(() => {
    if (viewMode !== 'year') return {};
    const totals = {};
    filteredEmployees.forEach(emp => {
      totals[emp.id] = 0;
      for (let m = 1; m <= 12; m++) {
        const daysInM = new Date(year, m, 0).getDate();
        for (let d = 1; d <= daysInM; d++) {
          if (leaveMap[emp.id]?.[m]?.[d]) {
            totals[emp.id]++;
          }
        }
      }
    });
    return totals;
  }, [filteredEmployees, leaveMap, year, viewMode]);

  // Calculate monthly totals for yearly view
  const monthlyTotals = useMemo(() => {
    if (viewMode !== 'year') return {};
    const totals = {};
    for (let m = 1; m <= 12; m++) {
      totals[m] = 0;
      filteredEmployees.forEach(emp => {
        const daysInM = new Date(year, m, 0).getDate();
        for (let d = 1; d <= daysInM; d++) {
          if (leaveMap[emp.id]?.[m]?.[d]) {
            totals[m]++;
          }
        }
      });
    }
    return totals;
  }, [filteredEmployees, leaveMap, year, viewMode]);

  // Build leave ranges by employee and month for yearly view (matching Excel format)
  // Returns: { employeeId: { month: [{start_date, end_date, leave_type, rangeText}] } }
  const leaveRangesByMonth = useMemo(() => {
    if (viewMode !== 'year') return {};
    
    const ranges = {};
    leaves.forEach(leave => {
      const empId = leave.employee_id;
      if (!ranges[empId]) ranges[empId] = {};
      
      const startDate = new Date(leave.start_date);
      const endDate = new Date(leave.end_date);
      
      // Check if this leave falls in the current year
      if (startDate.getFullYear() > year || endDate.getFullYear() < year) return;
      
      // Determine which months this leave spans
      const monthsSpanned = new Set();
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        if (d.getFullYear() === year) {
          monthsSpanned.add(d.getMonth() + 1);
        }
      }
      
      // Format the date range text
      const formatDate = (date) => {
        const day = date.getDate().toString().padStart(2, '0');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return `${day} ${monthNames[date.getMonth()]}`;
      };
      
      const startFormatted = formatDate(startDate);
      const endFormatted = formatDate(endDate);
      
      // Create range text based on leave type
      let rangeText = '';
      const leaveTypeLabel = LEAVE_TYPES[leave.leave_type]?.label || leave.leave_type;
      
      if (startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear()) {
        // Same month
        rangeText = `${startDate.getDate().toString().padStart(2, '0')} - ${endDate.getDate().toString().padStart(2, '0')}`;
      } else {
        // Different months
        rangeText = `${startFormatted} - ${endFormatted}`;
      }
      
      // Add leave type suffix for non-annual leaves
      if (leave.leave_type !== 'annual') {
        const typeCode = LEAVE_TYPES[leave.leave_type]?.code || leave.leave_type.substring(0, 2).toUpperCase();
        rangeText = `(${rangeText} ${typeCode})`;
      }
      
      // Add to each month the leave spans
      monthsSpanned.forEach(m => {
        if (!ranges[empId][m]) ranges[empId][m] = [];
        ranges[empId][m].push({
          start_date: leave.start_date,
          end_date: leave.end_date,
          leave_type: leave.leave_type,
          rangeText: rangeText,
          days: leave.days || ((endDate - startDate) / (1000 * 60 * 60 * 24) + 1)
        });
      });
    });
    
    return ranges;
  }, [leaves, year, viewMode]);

  // Navigate months
  const goToPrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const goToNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  // Download calendar as Excel
  const downloadExcel = async () => {
    try {
      let url;
      if (viewMode === 'year') {
        url = `${API_URL}/api/leaves/calendar/export/year/${year}?format=excel${departmentFilter !== 'all' ? `&department=${encodeURIComponent(departmentFilter)}` : ''}`;
      } else {
        url = `${API_URL}/api/leaves/calendar/export/${year}/${month}?format=excel${departmentFilter !== 'all' ? `&department=${encodeURIComponent(departmentFilter)}` : ''}`;
      }
      window.open(url, '_blank');
      toast({ title: 'Downloading', description: `Leave calendar Excel for ${viewMode === 'year' ? year : MONTHS[month-1].label + ' ' + year} downloading...` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download', variant: 'destructive' });
    }
  };

  // Download calendar as PDF
  const downloadPDF = async () => {
    try {
      let url;
      if (viewMode === 'year') {
        url = `${API_URL}/api/leaves/calendar/export/year/${year}?format=pdf${departmentFilter !== 'all' ? `&department=${encodeURIComponent(departmentFilter)}` : ''}`;
      } else {
        url = `${API_URL}/api/leaves/calendar/export/${year}/${month}?format=pdf${departmentFilter !== 'all' ? `&department=${encodeURIComponent(departmentFilter)}` : ''}`;
      }
      window.open(url, '_blank');
      toast({ title: 'Downloading', description: `Leave calendar PDF for ${viewMode === 'year' ? year : MONTHS[month-1].label + ' ' + year} downloading...` });
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to download', variant: 'destructive' });
    }
  };

  // Grand total of all leaves
  const grandTotal = viewMode === 'year' 
    ? Object.values(yearlyEmployeeTotals).reduce((sum, val) => sum + val, 0)
    : Object.values(employeeTotals).reduce((sum, val) => sum + val, 0);

  return (
    <TooltipProvider>
      <div className="space-y-4" data-testid="leave-calendar-page">
        {/* Header */}
        <div className="flex flex-wrap justify-between items-center gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Calendar className="h-8 w-8 text-green-600" />
              Leave Calendar
            </h1>
            <p className="text-gray-600 mt-1">
              Employee absence schedule for {viewMode === 'year' ? year : MONTHS[month-1].label + ' ' + year}
            </p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Department Filter */}
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-40">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 bg-white border rounded-lg p-1">
              <Button 
                variant={viewMode === 'month' ? 'default' : 'ghost'} 
                size="sm" 
                onClick={() => setViewMode('month')}
                className={viewMode === 'month' ? 'bg-green-600 hover:bg-green-700' : ''}
                data-testid="view-month-btn"
              >
                Month
              </Button>
              <Button 
                variant={viewMode === 'year' ? 'default' : 'ghost'} 
                size="sm" 
                onClick={() => setViewMode('year')}
                className={viewMode === 'year' ? 'bg-green-600 hover:bg-green-700' : ''}
                data-testid="view-year-btn"
              >
                Year
              </Button>
            </div>

            {/* Month Navigation - only show for monthly view */}
            {viewMode === 'month' && (
              <div className="flex items-center gap-2 bg-white border rounded-lg p-1">
                <Button variant="ghost" size="sm" onClick={goToPrevMonth}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <Select value={month.toString()} onValueChange={(v) => setMonth(parseInt(v))}>
                  <SelectTrigger className="w-32 border-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map(m => (
                      <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                  <SelectTrigger className="w-20 border-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Button variant="ghost" size="sm" onClick={goToNextMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Year Selector - only show for yearly view */}
            {viewMode === 'year' && (
              <div className="flex items-center gap-2 bg-white border rounded-lg p-1">
                <Button variant="ghost" size="sm" onClick={() => setYear(year - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Select value={year.toString()} onValueChange={(v) => setYear(parseInt(v))}>
                  <SelectTrigger className="w-24 border-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => setYear(year + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}

            <Button onClick={downloadPDF} variant="outline" className="text-red-600 border-red-300" data-testid="download-pdf-btn">
              <Download className="h-4 w-4 mr-2" />
              PDF
            </Button>
            <Button onClick={downloadExcel} variant="outline" className="text-green-600 border-green-300" data-testid="download-excel-btn">
              <Download className="h-4 w-4 mr-2" />
              Excel
            </Button>
          </div>
        </div>

        {/* Color Key Legend */}
        <Card>
          <CardContent className="py-3">
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span className="font-medium text-gray-600">Absence Type Key:</span>
              {Object.entries(LEAVE_TYPES).map(([key, type]) => (
                <div key={key} className="flex items-center gap-1">
                  <span className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold ${type.color} ${type.textColor}`}>
                    {type.code}
                  </span>
                  <span className="text-gray-600">{type.label}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Calendar Grid */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <span className="text-xl text-green-800">
                {viewMode === 'year' ? `${year} - Annual Absence Summary` : `${MONTHS[month-1].label} ${year} - Dates of Absence`}
              </span>
              <Badge variant="outline" className="text-base">
                {filteredEmployees.filter(e => leaveMap[e.id] && Object.keys(leaveMap[e.id]).length > 0).length} Employees | {grandTotal} Total Leave Days
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading calendar...</div>
            ) : viewMode === 'year' ? (
              /* YEARLY VIEW - Excel-style with date ranges */
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    {/* Month headers row */}
                    <tr className="bg-amber-900 text-white">
                      <th className="p-2 text-left sticky left-0 bg-amber-900 z-20 min-w-[200px] border-r">
                        Employee Name
                      </th>
                      {MONTHS.map(m => (
                        <th key={m.value} className="p-2 text-center min-w-[150px] border-l border-amber-700">
                          {m.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.map((employee, idx) => {
                      const employeeLeaves = leaveRangesByMonth[employee.id] || {};
                      return (
                        <tr 
                          key={employee.id} 
                          className={`border-b hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                        >
                          <td className={`p-2 sticky left-0 z-10 border-r ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} style={{ maxWidth: '200px' }}>
                            <p className="font-medium text-gray-800 break-words whitespace-normal leading-tight">{employee.name}</p>
                          </td>
                          {MONTHS.map(m => {
                            const monthLeaves = employeeLeaves[m.value] || [];
                            
                            // Group leaves by type for display
                            const leaveTexts = [];
                            const seenRanges = new Set();
                            
                            monthLeaves.forEach(leave => {
                              const key = `${leave.start_date}-${leave.end_date}`;
                              if (!seenRanges.has(key)) {
                                seenRanges.add(key);
                                leaveTexts.push(leave);
                              }
                            });
                            
                            return (
                              <td key={m.value} className="p-1.5 border-l align-top overflow-hidden" style={{ maxWidth: '150px' }}>
                                {leaveTexts.length > 0 ? (
                                  <div className="flex flex-col items-center gap-1 w-full">
                                    {leaveTexts.map((leave, i) => {
                                      const typeInfo = LEAVE_TYPES[leave.leave_type];
                                      const isSpecialLeave = leave.leave_type !== 'annual';
                                      return (
                                        <Tooltip key={i}>
                                          <TooltipTrigger asChild>
                                            <div 
                                              className={`text-xs leading-tight px-1.5 py-0.5 rounded text-center w-full break-words whitespace-normal ${
                                                isSpecialLeave 
                                                  ? `${typeInfo?.color || 'bg-gray-500'} ${typeInfo?.textColor || 'text-white'}`
                                                  : 'text-gray-700'
                                              }`}
                                            >
                                              {leave.rangeText}
                                            </div>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            <p className="font-bold">{employee.name}</p>
                                            <p>{typeInfo?.label || leave.leave_type}</p>
                                            <p>{leave.days} days</p>
                                          </TooltipContent>
                                        </Tooltip>
                                      );
                                    })}
                                  </div>
                                ) : null}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              /* MONTHLY VIEW */
              <div className="overflow-x-auto overflow-y-auto max-h-[70vh]">
                <table className="w-full text-sm border-collapse">
                  <thead className="sticky top-0 z-30">
                    {/* Day of week row */}
                    <tr className="bg-gray-100">
                      <th className="p-2 text-left sticky left-0 bg-gray-100 z-40 min-w-[180px] border-r">
                        Employee Name
                      </th>
                      {calendarDays.map(({ day, dayOfWeek, isWeekend }) => (
                        <th 
                          key={day} 
                          className={`p-1 text-center min-w-[32px] text-xs ${isWeekend ? 'bg-gray-200' : 'bg-gray-100'}`}
                        >
                          {dayOfWeek}
                        </th>
                      ))}
                      <th className="p-2 text-center min-w-[60px] bg-gray-100 border-l">Total</th>
                    </tr>
                    {/* Day number row */}
                    <tr className="bg-amber-900 text-white">
                      <th className="p-2 text-left sticky left-0 bg-amber-900 z-40 border-r">
                        
                      </th>
                      {calendarDays.map(({ day, isWeekend }) => (
                        <th 
                          key={day} 
                          className={`p-1 text-center min-w-[32px] font-bold ${isWeekend ? 'bg-amber-800' : ''}`}
                        >
                          {day}
                        </th>
                      ))}
                      <th className="p-2 text-center bg-amber-900 border-l">Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEmployees.filter(emp => leaveMap[emp.id] && Object.keys(leaveMap[emp.id]).length > 0).map((employee, idx) => (
                      <tr 
                        key={employee.id} 
                        className={`border-b hover:bg-gray-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
                      >
                        <td className={`p-2 sticky left-0 z-10 border-r ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                          <div>
                            <p className="font-medium text-gray-800">{employee.name}</p>
                            <p className="text-xs text-gray-500">{employee.department || 'No Dept'}</p>
                          </div>
                        </td>
                        {calendarDays.map(({ day, isWeekend }) => {
                          const leaveType = leaveMap[employee.id]?.[day];
                          const typeInfo = leaveType ? LEAVE_TYPES[leaveType] : null;
                          
                          return (
                            <td 
                              key={day} 
                              className={`p-0 text-center ${isWeekend ? 'bg-gray-100' : ''}`}
                            >
                              {typeInfo ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className={`w-full h-8 flex items-center justify-center ${typeInfo.color} ${typeInfo.textColor} font-bold text-xs cursor-default`}>
                                      {typeInfo.code}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>{employee.name}</p>
                                    <p className="font-bold">{typeInfo.label}</p>
                                    <p>{MONTHS[month-1].label} {day}, {year}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <div className="w-full h-8"></div>
                              )}
                            </td>
                          );
                        })}
                        <td className={`p-2 text-center font-bold border-l ${employeeTotals[employee.id] > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                          {employeeTotals[employee.id] || 0}
                        </td>
                      </tr>
                    ))}
                    
                    {/* Daily Totals Row */}
                    <tr className="bg-green-50 border-t-2 border-green-300 font-bold">
                      <td className="p-2 sticky left-0 bg-green-50 z-10 border-r text-green-800">
                        {MONTHS[month-1].label} Total
                      </td>
                      {calendarDays.map(({ day }) => (
                        <td key={day} className="p-1 text-center text-green-700">
                          {dailyTotals[day] > 0 ? dailyTotals[day] : ''}
                        </td>
                      ))}
                      <td className="p-2 text-center text-green-800 border-l bg-green-100">
                        {grandTotal}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {Object.entries(LEAVE_TYPES).map(([key, type]) => {
            const count = leaves.filter(l => l.leave_type === key).reduce((sum, l) => sum + (l.days || 0), 0);
            return (
              <Card key={key} className="text-center">
                <CardContent className="py-3">
                  <div className={`w-8 h-8 rounded mx-auto mb-1 flex items-center justify-center ${type.color} ${type.textColor} font-bold`}>
                    {type.code}
                  </div>
                  <p className="text-lg font-bold">{count}</p>
                  <p className="text-xs text-gray-500">{type.label}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </TooltipProvider>
  );
};

export default LeaveCalendar;
